import {
  MANAGEMENTS_STATE_ERROR,
  MANAGEMENTS_STATE_PENDING,
  MANAGEMENTS_STATE_REJECTED,
  MANAGEMENTS_STATE_RESOLVED,
  MANAGEMENTS_REGISTER_TYPE_REQUEST,
  MANAGEMENTS_SUBTYPE_UPDATE_POWERS_TEXT,
  MANAGEMENTS_SUBTYPE_UPDATE_HOLDER_TEXT,
  MANAGEMENTS_SUBTYPE_CONTRACT_END_TEXT,
  MANAGEMENTS_SUBTYPE_UPDATE_ADDRESS_TEXT,
  MANAGEMENTS_SUBTYPE_UPDATE_CONTACT_TEXT,
  MANAGEMENTS_REGISTER_TYPE_CLAIMS,
  MANAGEMENTS_SUBTYPE_UPDATE_CLAIM_TEXT,
  SERVICES_SUBTYPE_OPTIMIZE_COVERAGES,
  SERVICES_SUBTYPE_OPTIMIZE_POWER,
  SERVICES_SUBTYPE_OPTIMIZE_GAS,
  SERVICES_SUBTYPE_OPTIMIZE_BILLS,
  MANAGEMENTS_SUBTYPE_UPDATE_BANK_ACCOUNT_TEXT,
  MANAGEMENTS_SUBTYPE_UPDATE_GAS_FLOW_TEXT,
  MANAGEMENTS_STATE_OPEN,
  MANAGEMENTS_INCIDENCE_TEXT_UPDATE_CONTACT,
  MANAGEMENTS_INCIDENCE_TEXT_UPDATE_DELIVERY_DATA,
  MANAGEMENTS_STATE_PENDING_DISTRIBUTION,
  MANAGEMENTS_STATE_PENDING_COMMERCIAL,
  MANAGEMENTS_STATE_PENDING_CLIENT,
  RECIPIENT_IDS,
  MANAGEMENTS_STATE_UNSOLVED,
  RECIPIENT_IDS_LABEL,
  MANAGEMENTS_SUBTYPE_UPDATE_DOMICILIATION,
  MANAGEMENTS_STATE_BANK_ERROR,
} from "../../../core/constants/constants";
import {
  Management,
  ManagementRequestInfo,
  CallCenter,
  FormattedManagementsList,
  GetManagementDetailRequestResponse,
  ManagementDetail,
  RequestStateDescriptionData,
  ManagementsStateData,
  ManagementCodeText,
  ManagementCupsInfo,
} from "../../../core/models/managements/managements.model";
import { ManagementsList } from "../../../core/models/managements/managements.model";
import { formatStringDate } from "../../dateUtils/formatDate.util";
import theme from "../../../muiTheme";
import { ManagementUpdateCup } from "../../../core/models/cupsCifSelector/cupsCifSelector.model";
import { OwnerDataCups } from "../../../core/models/ownerData/ownerData.model";
import { HistoricFilters } from "../../../core/models/managements/managements.model";
import { AGENT_MANAGEMENT_SERVICE_CONSTANTS } from "../../../core/constants/agentManagements.constants";

const formatManagements = (managements: Management[]): ManagementsList[] =>
  managements.map(
    ({
      fechaPeticion,
      fechaVigencia,
      codigoGestion,
      estadoGestion,
      descripciónGestion,
      idGestion,
      cupsGestion,
    }) => ({
      managementId: idGestion,
      managementCode: codigoGestion,
      dateRequest: formatStringDate(fechaPeticion),
      effectiveDate: formatStringDate(fechaVigencia),
      managementState: estadoGestion,
      managementDescription: descripciónGestion,
      managementCups: cupsGestion,
    }),
  );

const getEnvironmentFromRegisterType = (type: number): string => {
  switch (type) {
    case 2:
    case 1:
    case 8:
      return "luz";

    case 3:
    case 4:
    case 7:
      return "gas";

    default:
      return "";
  }
};

const getEnvironmentFromSubtype = (subtype: number): string => {
  switch (subtype) {
    case 56:
    case 55:
    case 58:
    case 213:
    case 8:
    case 10:
    case 9:
    case 1:
    case 16:
    case 57:
    case 250:
    case 253:
      return "luz";

    case 64:
    case 63:
    case 66:
    case 214:
    case 33:
    case 35:
    case 34:
    case 31:
    case 40:
    case 65:
    case 255:
    case 258:
      return "gas";

    default:
      return "";
  }
};

const formatManagementsList = (
  callCenters: CallCenter[],
): FormattedManagementsList[] =>
  callCenters.map(
    ({
      NumeroIncidencia,
      Fecha,
      Estado,
      TextoTipoRegistro,
      TextoSubTipoReclamacion,
      TextoIncidencia,
      CodigoCups,
      Cliente,
      IdSubTipoReclamacion,
      IdTipoRegistro,
      Numero,
      NombreVia,
      Identidad,
    }) => {
      const environment =
        getEnvironmentFromSubtype(IdSubTipoReclamacion) !== ""
          ? getEnvironmentFromSubtype(IdSubTipoReclamacion)
          : getEnvironmentFromRegisterType(IdTipoRegistro);
      return {
        issueNumber: NumeroIncidencia,
        date: Fecha,
        state: Estado,
        recordTextType: TextoTipoRegistro,
        claimTextSubType: TextoSubTipoReclamacion,
        incidenceText: TextoIncidencia,
        cups: CodigoCups,
        address: `${NombreVia}${Numero ? ", " + Numero : ""}`,
        environment: environment,
        socialReason: Cliente,
        idSubType: IdSubTipoReclamacion,
        cif: Identidad,
      };
    },
  );

const formatManagementDetail = ({
  NumeroIncidencia,
  Fecha,
  FechaCierre,
  Estado,
  CodigoCups,
  Documentos,
  TextoSolucion,
  TextoIncidencia,
  TextoTipoRegistro,
  IdSubTipoReclamacion,
  IdTipoRegistro,
  Numero,
  NombreVia,
}: GetManagementDetailRequestResponse): ManagementDetail => {
  const environment =
    getEnvironmentFromSubtype(IdSubTipoReclamacion) !== ""
      ? getEnvironmentFromSubtype(IdSubTipoReclamacion)
      : getEnvironmentFromRegisterType(IdTipoRegistro);

  return {
    refferenceNumber: NumeroIncidencia,
    registrarionDate: formatStringDate(Fecha || ""),
    closingDate: formatStringDate(FechaCierre || ""),
    cupsCode: CodigoCups,
    state: Estado,
    solutionText: TextoSolucion
      ? TextoSolucion.replace("\n", "").split("\r")
      : [],
    incidenceText: TextoIncidencia,
    documents: Documentos.map((doc) => ({
      IdCallCenterDocumento: doc.IdCallCenterDocumento,
      IdDocumento: doc.IdDocumento,
      FileName: doc.FileName,
      Nombre: doc.Nombre,
    })),
    registerType: TextoTipoRegistro,
    environment,
    address: `${NombreVia}${Numero ? ", " + Numero : ""}`,
  };
};

const formatOwnerDataCup = ({
  codeCups,
  address,
  idCups,
  idContract,
  cif,
  envirorment,
  contractCode,
  situation,
  date,
}: OwnerDataCups): ManagementUpdateCup => ({
  cupsCode: codeCups,
  supplyAddress: address,
  cupsId: idCups,
  cupsContractId: idContract,
  cupsAssociatedCif: cif,
  envirorment,
  cupsContractCode: contractCode,
  situation,
  date,
});

const getRegisterTypeId = (
  envirorment: string,
  registerType: string,
): number => {
  if (envirorment === "Luz") {
    return registerType === MANAGEMENTS_REGISTER_TYPE_REQUEST ? 2 : 1;
  }

  return registerType === MANAGEMENTS_REGISTER_TYPE_REQUEST ? 3 : 4;
};

const getIncidenceIconTitle = (
  incedenceTexts: string[],
  incidenceText: string,
) => {
  const text =
    incedenceTexts.find((incidenceMatchingText) =>
      incidenceText.includes(incidenceMatchingText),
    ) ?? "";

  switch (text) {
    case MANAGEMENTS_INCIDENCE_TEXT_UPDATE_CONTACT:
      return {
        title: "Actualizar datos de contacto",
        icon: "t-icon-AddressBook",
      };
    case MANAGEMENTS_INCIDENCE_TEXT_UPDATE_DELIVERY_DATA:
      return {
        title: "Actualizar dirección de correspondencia",
        icon: "t-icon-home",
      };
    default:
      return { title: "Otras gestiones", icon: "t-icon-warning" };
  }
};

export const tpiManagementsTitles = Object.values(
  AGENT_MANAGEMENT_SERVICE_CONSTANTS,
)
  .map(({ titleKey, servicePayloadData }) => {
    const dataGas = {
      title: titleKey,
      idSubType: servicePayloadData.GAS.subTypeReclamationId,
      icon: "t-icon-warning",
    };

    const dataElectricity = {
      title: titleKey,
      idSubType: servicePayloadData.ELECTRICITY.subTypeReclamationId,
      icon: "t-icon-warning",
    };

    const allData = [dataGas, dataElectricity].filter(
      (item) => item.title !== "",
    );
    return allData;
  })
  .flatMap((item) => item);

const clientManagementsTitles = RECIPIENT_IDS_LABEL.map((item) => {
  return {
    title: item.label,
    idSubType: item.idSubtipoReclamacion,
    icon: item.icon,
  };
});

const getManagementTitleText = (
  recordTextType: string,
  claimTextSubType: string,
  idSubType?: number,
) => {
  if (idSubType) {
    return getManagementSubtypeTextIcon(idSubType).title;
  }

  return recordTextType === MANAGEMENTS_REGISTER_TYPE_CLAIMS
    ? recordTextType
    : getManagementCodeTextIcon(claimTextSubType).title;
};

const getManagementSubtypeTextIcon = (idSubType: number) => {
  const tpiClientManagementTitles =
    clientManagementsTitles.concat(tpiManagementsTitles);

  const foundItem = tpiClientManagementTitles.find(
    (item) => item.idSubType === idSubType,
  );

  if (foundItem) {
    return {
      title: foundItem.title,
      icon: foundItem.icon,
    };
  }

  return {
    title: "Otras gestiones",
    icon: "t-icon-warning",
  };
};

const getManagementCodeTextIcon = (
  managementText: string,
): { title: string; icon: string } => {
  switch (managementText) {
    case MANAGEMENTS_SUBTYPE_UPDATE_POWERS_TEXT:
      return {
        title: "Modificacion de potencias",
        icon: "t-icon-electricite-big",
      };
    case MANAGEMENTS_SUBTYPE_UPDATE_GAS_FLOW_TEXT:
      return {
        title: "Modificación de caudal diario (Qd)",
        icon: "t-icon-gaz-big",
      };
    case MANAGEMENTS_SUBTYPE_UPDATE_HOLDER_TEXT:
      return {
        title: "Cambio de titular",
        icon: "t-icon-building",
      };
    case MANAGEMENTS_SUBTYPE_UPDATE_ADDRESS_TEXT:
      return {
        title: "Actualizar dirección de correspondencia",
        icon: "t-icon-home",
      };
    case MANAGEMENTS_SUBTYPE_UPDATE_BANK_ACCOUNT_TEXT:
      return {
        title: "Cambio de domiciliación bancaria",
        icon: "t-icon-Bank",
      };
    case MANAGEMENTS_SUBTYPE_CONTRACT_END_TEXT:
      return {
        title: "Baja por cese de actividad",
        icon: "t-icon-power-off",
      };
    case MANAGEMENTS_SUBTYPE_UPDATE_CLAIM_TEXT:
      return {
        title: "Reclamación",
        icon: "t-icon-warning",
      };
    case MANAGEMENTS_SUBTYPE_UPDATE_CONTACT_TEXT:
      return {
        title: "Actualizar datos de contacto",
        icon: "t-icon-AddressBook",
      };
    case "Habilitar / Deshabilitar Factura en Papel":
      return {
        title: "Actualizar envío de factura",
        icon: "t-icon-file",
      };
    default:
      return { title: "Otras gestiones", icon: "t-icon-warning" };
  }
};

const managementCodeText: ManagementCodeText[] = [
  {
    id: [RECIPIENT_IDS[0].idSubtipoReclamacion],
    label: "Modificacion de potencias",
  },
  {
    id: [RECIPIENT_IDS[1].idSubtipoReclamacion],
    label: "Modificación de caudal diario (Qd)",
  },
  {
    id: [
      RECIPIENT_IDS[2].idSubtipoReclamacion,
      RECIPIENT_IDS[3].idSubtipoReclamacion,
    ],
    label: "Cambio de titular",
  },
  {
    id: [
      RECIPIENT_IDS[4].idSubtipoReclamacion,
      RECIPIENT_IDS[5].idSubtipoReclamacion,
    ],
    label: "Cambio de domiciliación bancaria",
  },
  {
    id: [
      RECIPIENT_IDS[6].idSubtipoReclamacion,
      RECIPIENT_IDS[7].idSubtipoReclamacion,
    ],
    label: "Actualizar dirección de correspondencia",
  },
  {
    id: RECIPIENT_IDS.slice(8, 18).map((item) => item.idSubtipoReclamacion),
    label: "Reclamación",
  },
  {
    id: [
      RECIPIENT_IDS[18].idSubtipoReclamacion,
      RECIPIENT_IDS[19].idSubtipoReclamacion,
    ],
    label: "Baja por cese de actividad",
  },
  {
    id: [RECIPIENT_IDS[20].idSubtipoReclamacion],
    label: "Optimizador de potencias",
  },
  {
    id: [RECIPIENT_IDS[21].idSubtipoReclamacion],
    label: "Optimizador de caudal",
  },
  {
    id: [
      RECIPIENT_IDS[22].idSubtipoReclamacion,
      RECIPIENT_IDS[23].idSubtipoReclamacion,
    ],
    label: "Simulador de facturas",
  },
];

const getManagementStateData = (
  managementState: string,
): ManagementsStateData => {
  switch (managementState.toLowerCase()) {
    case MANAGEMENTS_STATE_PENDING.toLowerCase():
    case MANAGEMENTS_STATE_PENDING_COMMERCIAL.toLowerCase():
      return {
        color: theme.palette.info.contrastText,
        backgroundColor: theme.palette.info.light,
        icon: "t-icon-clock",
        label: "Gestiones.Estados.En curso",
      };

    case MANAGEMENTS_STATE_PENDING_DISTRIBUTION.toLowerCase():
      return {
        color: theme.palette.info.contrastText,
        backgroundColor: theme.palette.info.light,
        icon: "t-icon-clock",
        label: "Gestiones.Estados.Pendiente Distribuidora",
      };

    case MANAGEMENTS_STATE_PENDING_CLIENT.toLowerCase():
      return {
        color: theme.palette.info.contrastText,
        backgroundColor: theme.palette.info.light,
        icon: "t-icon-clock",
        label: "Gestiones.Estados.Pendiente Respuesta Cliente",
      };
    case MANAGEMENTS_STATE_OPEN.toLowerCase():
      return {
        color: theme.palette.info.contrastText,
        backgroundColor: theme.palette.info.light,
        icon: "t-icon-clock",
        label: "Gestiones.Estados.Abierta",
      };
    case MANAGEMENTS_STATE_RESOLVED.toLowerCase():
    case MANAGEMENTS_STATE_UNSOLVED.toLowerCase():
      return {
        color: theme.palette.success.contrastText,
        backgroundColor: theme.palette.success.light,
        icon: "t-icon-check",
        label: "Gestiones.Estados.Resuelta",
      };
    case MANAGEMENTS_STATE_REJECTED.toLowerCase():
      return {
        color: theme.palette.error.contrastText,
        backgroundColor: theme.palette.error.light,
        icon: "t-icon-close",
        label: "Gestiones.Estados.Rechazado",
      };
    default:
      return { color: "", backgroundColor: "", icon: "", label: "" };
  }
};

const getIbanStateData = (
  isNewIban: boolean,
  isActive: boolean,
): ManagementsStateData => {
  if (isNewIban) {
    return {
      color: theme.palette.info.contrastText,
      backgroundColor: theme.palette.info.light,
      icon: "t-icon-clock",
      label: "Gestiones.Estado IBAN.Nuevo",
    };
  }

  if (isActive) {
    return {
      color: theme.palette.success.contrastText,
      backgroundColor: theme.palette.success.light,
      icon: "t-icon-check",
      label: "Gestiones.Estado IBAN.Activo",
    };
  } else {
    return {
      color: theme.palette.error.contrastText,
      backgroundColor: theme.palette.error.light,
      icon: "t-icon-close",
      label: "Gestiones.Estado IBAN.Inactivo",
    };
  }
};

const getManagementsRequestTypeData = (
  requestType: number | string,
  requestState: string,
  isAgent: boolean = false,
  isManagementDetails: boolean = false,
) => {
  switch (requestType) {
    case MANAGEMENTS_SUBTYPE_UPDATE_CONTACT_TEXT:
    case MANAGEMENTS_INCIDENCE_TEXT_UPDATE_CONTACT:
      switch (requestState) {
        case MANAGEMENTS_STATE_RESOLVED:
          return {
            title:
              "Gestiones.Se ha actualizado con éxito los datos de contacto",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
          };
        case MANAGEMENTS_STATE_ERROR:
          return {
            title: "Gestiones.Parece que ha habido un error",
            description:
              "Gestiones.Parece que ha habido un error, prueba de nuevo más tarde",
          };
        default:
          return { title: "", description: "" };
      }

    case MANAGEMENTS_SUBTYPE_UPDATE_POWERS_TEXT:
      switch (requestState) {
        case MANAGEMENTS_STATE_PENDING:
          return {
            title: "Gestiones.Estamos procesando su solicitud",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
            descriptionExtra: "",
          };
        case MANAGEMENTS_STATE_OPEN:
          return {
            title: "Gestiones.Estamos procesando su solicitud",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
            descriptionExtra: "",
          };
        case MANAGEMENTS_STATE_RESOLVED:
          return {
            title: "Gestiones.Se ha realizado con éxito el cambio de potencia",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
          };
        case MANAGEMENTS_STATE_REJECTED:
          return {
            title:
              "Gestiones.Lo sentimos, no se ha podido realizar el cambio de potencia",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
          };
        case MANAGEMENTS_STATE_ERROR:
          return {
            title: "Gestiones.Parece que ha habido un error",
            description:
              "Gestiones.Parece que ha habido un error, prueba de nuevo más tarde",
          };
        default:
          return {
            title: "Gestiones.Estamos procesando su solicitud",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
            descriptionExtra: "",
          };
      }

    case MANAGEMENTS_SUBTYPE_UPDATE_GAS_FLOW_TEXT:
      switch (requestState) {
        case MANAGEMENTS_STATE_PENDING:
        case MANAGEMENTS_STATE_OPEN:
          return {
            title: "Gestiones.Estamos procesando su solicitud",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
            descriptionExtra: "",
          };
        case MANAGEMENTS_STATE_RESOLVED:
          return {
            title: "Gestiones.Se ha realizado con éxito el cambio de caudal",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
          };
        case MANAGEMENTS_STATE_REJECTED:
          return {
            title:
              "Gestiones.Lo sentimos, no se ha podido realizar el cambio de caudal",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
          };
        case MANAGEMENTS_STATE_ERROR:
          return {
            title: "Gestiones.Parece que ha habido un error",
            description:
              "Gestiones.Parece que ha habido un error, prueba de nuevo más tarde",
          };
        default:
          return {
            title: "Gestiones.Estamos procesando su solicitud",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
            descriptionExtra: "",
          };
      }

    case MANAGEMENTS_SUBTYPE_UPDATE_BANK_ACCOUNT_TEXT:
      switch (requestState) {
        case MANAGEMENTS_STATE_PENDING:
        case MANAGEMENTS_STATE_OPEN:
          return {
            title: "Gestiones.Estamos procesando su solicitud",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
            descriptionExtra: "",
          };
        case MANAGEMENTS_STATE_RESOLVED:
          return {
            title:
              "Gestiones.Se ha realizado con éxito el cambio de domiciliación",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
          };
        case MANAGEMENTS_STATE_REJECTED:
          return {
            title:
              "Gestiones.Lo sentimos, no se ha podido realizar el cambio de domiciliación",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
          };
        case MANAGEMENTS_STATE_ERROR:
          return {
            title: "Gestiones.Parece que ha habido un error",
            description:
              "Gestiones.Parece que ha habido un error, prueba de nuevo más tarde",
          };
        default:
          return {
            title: "Gestiones.Estamos procesando su solicitud",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
            descriptionExtra: "",
          };
      }

    case MANAGEMENTS_SUBTYPE_UPDATE_CLAIM_TEXT:
      switch (requestState) {
        case MANAGEMENTS_STATE_PENDING:
        case MANAGEMENTS_STATE_OPEN:
          return {
            title: "Gestiones.Hemos recibido su reclamación",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
            descriptionExtra: "",
          };
        case MANAGEMENTS_STATE_RESOLVED:
          return {
            title: "Gestiones.Hemos dado respuesta a su reclamación",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
          };
        case MANAGEMENTS_STATE_REJECTED:
          return {
            title:
              "Gestiones.Lo sentimos, no se ha podido procesar su reclamación",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
          };
        case MANAGEMENTS_STATE_ERROR:
          return {
            title: "Gestiones.Parece que ha habido un error",
            description:
              "Gestiones.Parece que ha habido un error, prueba de nuevo más tarde",
          };
        default:
          return {
            title: "Gestiones.Hemos recibido su reclamación",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
            descriptionExtra: "",
          };
      }

    case MANAGEMENTS_SUBTYPE_UPDATE_ADDRESS_TEXT:
    case MANAGEMENTS_INCIDENCE_TEXT_UPDATE_DELIVERY_DATA:
      switch (requestState) {
        case MANAGEMENTS_STATE_RESOLVED:
          return {
            title:
              "Gestiones.Se ha actualizado con éxito la dirección de correspondencia",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
          };
        case MANAGEMENTS_STATE_PENDING:
          return {
            title: "Gestiones.Estamos procesando su solicitud",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
            descriptionExtra: "",
          };
        case MANAGEMENTS_STATE_ERROR:
          return {
            title: "Gestiones.Parece que ha habido un error",
            description:
              "Gestiones.Parece que ha habido un error, prueba de nuevo más tarde",
          };
        default:
          return {
            title:
              "Gestiones.Se ha actualizado con éxito la dirección de correspondencia",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
          };
      }

    case MANAGEMENTS_SUBTYPE_UPDATE_DOMICILIATION:
      switch (requestState) {
        case MANAGEMENTS_STATE_RESOLVED:
          return {
            title:
              "Gestiones.Se ha realizado con éxito el cambio de domiciliación bancaria",
            description: `Gestiones.Descripción cambio de domiciliación`,
          };
        case MANAGEMENTS_STATE_PENDING:
          return {
            title: "Gestiones.Estamos procesando su solicitud",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
            descriptionExtra: "",
          };
        case MANAGEMENTS_STATE_ERROR:
          return {
            title: "Gestiones.Parece que ha habido un error",
            description:
              "Gestiones.Parece que ha habido un error, prueba de nuevo más tarde",
          };
        case MANAGEMENTS_STATE_BANK_ERROR:
          return {
            title:
              "Gestiones.Lo sentimos, no se ha podido procesar su solicitud",
            description: `Gestiones.No ha sido posible realizar la operación solicitada${
              isAgent ? " agentes" : ""
            }`,
          };
        default:
          return {
            title:
              "Gestiones.Se ha realizado con éxito el cambio de domiciliación bancaria",
            description: `Gestiones.Descripción cambio de domiciliación`,
          };
      }

    case MANAGEMENTS_SUBTYPE_UPDATE_HOLDER_TEXT:
      switch (requestState) {
        case MANAGEMENTS_STATE_PENDING:
        case MANAGEMENTS_STATE_OPEN:
          return {
            title: "Gestiones.Estamos procesando su solicitud",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
            descriptionExtra: "",
          };
        case MANAGEMENTS_STATE_RESOLVED:
          return {
            title: "Gestiones.Se ha realizado con éxito el cambio de titular",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
          };
        case MANAGEMENTS_STATE_REJECTED:
          return {
            title:
              "Gestiones.Lo sentimos, no se ha podido procesar su solicitud",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
          };
        case MANAGEMENTS_STATE_ERROR:
          return {
            title: "Gestiones.Parece que ha habido un error",
            description:
              "Gestiones.Parece que ha habido un error, prueba de nuevo más tarde",
          };
        default:
          return {
            title: "Gestiones.Estamos procesando su solicitud",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
            descriptionExtra: "",
          };
      }
    case MANAGEMENTS_SUBTYPE_CONTRACT_END_TEXT:
      switch (requestState) {
        case MANAGEMENTS_STATE_PENDING:
        case MANAGEMENTS_STATE_OPEN:
          return {
            title: "Gestiones.Hemos recibido su solicitud de baja",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
            descriptionExtra: "",
          };
        case MANAGEMENTS_STATE_RESOLVED:
          return {
            title: "Gestiones.Su suministro ha sido dado de baja",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
          };
        case MANAGEMENTS_STATE_REJECTED:
          return {
            title: "Gestiones.Lo sentimos no hemos podido procesar su baja",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
          };
        case MANAGEMENTS_STATE_ERROR:
          return {
            title: "Gestiones.Parece que ha habido un error",
            description:
              "Gestiones.Parece que ha habido un error, prueba de nuevo más tarde",
          };
        default:
          return {
            title: "Gestiones.Hemos recibido su solicitud de baja",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
            descriptionExtra: "",
          };
      }

    // ---- Services
    case SERVICES_SUBTYPE_OPTIMIZE_COVERAGES:
      switch (requestState) {
        case MANAGEMENTS_STATE_PENDING:
        case MANAGEMENTS_STATE_OPEN:
          return {
            title: "Servicios.Orden de cobertura solicitada",
            description: "Servicios.Procesando orden de cobertura",
            descriptionExtra:
              "Servicios.Se enviará respuesta a todos los contactos asociados a este suministro.",
          };
        case MANAGEMENTS_STATE_RESOLVED:
          return {
            title: "Servicios.Orden de cobertura procesada con éxito",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
          };
        case MANAGEMENTS_STATE_REJECTED:
          return {
            title:
              "Servicios.Lo sentimos, no se ha podido procesar la orden de cobertura",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
          };
        case MANAGEMENTS_STATE_ERROR:
          return {
            title: "Servicios.Parece que ha habido un error",
            description:
              "Servicios.Parece que ha habido un error, prueba de nuevo más tarde",
          };
        default:
          return {
            title: "Servicios.Orden de cobertura solicitada",
            description: "Servicios.Procesando orden de cobertura",
            descriptionExtra:
              "Servicios.Se enviará respuesta a todos los contactos asociados a este suministro.",
          };
      }
    case SERVICES_SUBTYPE_OPTIMIZE_POWER:
      switch (requestState) {
        case MANAGEMENTS_STATE_PENDING:
        case MANAGEMENTS_STATE_OPEN:
          return {
            title: "Servicios.Optimización de potencias solicitado",
            description: "Servicios.Procesando optimización de potencias",
            descriptionExtra:
              "Servicios.Se enviará respuesta a todos los contactos asociados a este suministro.",
          };
        case MANAGEMENTS_STATE_RESOLVED:
          return {
            title: "Servicios.Optimizador de potencias realizado correctamente",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
          };
        case MANAGEMENTS_STATE_REJECTED:
          return {
            title:
              "Servicios.Lo sentimos, no se ha podido realizar el optimizador de potencias",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
          };
        case MANAGEMENTS_STATE_ERROR:
          return {
            title: "Servicios.Parece que ha habido un error",
            description:
              "Servicios.Parece que ha habido un error, prueba de nuevo más tarde",
          };
        default:
          return {
            title: "Servicios.Optimización de potencias solicitado",
            description: "Servicios.Procesando optimización de potencias",
            descriptionExtra:
              "Servicios.Se enviará respuesta a todos los contactos asociados a este suministro.",
          };
      }
    case SERVICES_SUBTYPE_OPTIMIZE_GAS:
      switch (requestState) {
        case MANAGEMENTS_STATE_PENDING:
        case MANAGEMENTS_STATE_OPEN:
          return {
            title: "Servicios.Optimización de caudal solicitado",
            description: "Servicios.Procesando optimización de caudal",
            descriptionExtra:
              "Servicios.Se enviará respuesta a todos los contactos asociados a este suministro.",
          };
        case MANAGEMENTS_STATE_RESOLVED:
          return {
            title: "Servicios.Optimizador de caudal realizado correctamente",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
          };
        case MANAGEMENTS_STATE_REJECTED:
          return {
            title:
              "Servicios.Lo sentimos, no se ha podido realizar el optimizador de caudal",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
          };
        case MANAGEMENTS_STATE_ERROR:
          return {
            title: "Servicios.Parece que ha habido un error",
            description:
              "Servicios.Parece que ha habido un error, prueba de nuevo más tarde",
          };
        default:
          return {
            title: "Servicios.Optimización de caudal solicitado",
            description: "Servicios.Procesando optimización de caudal",
            descriptionExtra:
              "Servicios.Se enviará respuesta a todos los contactos asociados a este suministro.",
          };
      }
    case SERVICES_SUBTYPE_OPTIMIZE_BILLS:
      switch (requestState) {
        case MANAGEMENTS_STATE_PENDING:
        case MANAGEMENTS_STATE_OPEN:
          return {
            title: "Servicios.Simulación de facturas solicitado",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
            descriptionExtra: "",
          };
        case MANAGEMENTS_STATE_RESOLVED:
          return {
            title: "Servicios.Simulador de facturas realizado correctamente",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
          };
        case MANAGEMENTS_STATE_REJECTED:
          return {
            title:
              "Servicios.Lo sentimos, no se ha podido realizar la simulación de facturas",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
          };
        case MANAGEMENTS_STATE_ERROR:
          return {
            title: "Servicios.Parece que ha habido un error",
            description:
              "Servicios.Parece que ha habido un error, prueba de nuevo más tarde",
          };
        default:
          return {
            title: "Servicios.Simulación de facturas solicitado",
            description: `Gestiones.Descripción final gestión${
              isAgent ? " agentes" : ""
            }`,
            descriptionExtra: "",
          };
      }

    default:
      if (isManagementDetails) {
        switch (requestState) {
          case MANAGEMENTS_STATE_PENDING:
            return {
              title: "Gestiones.Estamos procesando su solicitud",
              description: `Gestiones.Descripción final gestión${
                isAgent ? " agentes" : ""
              }`,
              descriptionExtra: "",
            };
          case MANAGEMENTS_STATE_OPEN:
            return {
              title: "Gestiones.Estamos procesando su solicitud",
              description: `Gestiones.Descripción final gestión${
                isAgent ? " agentes" : ""
              }`,
              descriptionExtra: "",
            };
          case MANAGEMENTS_STATE_RESOLVED:
            return {
              title: "Gestiones.Su solicitud se ha procesado con éxito",
              description: `Gestiones.Descripción final gestión${
                isAgent ? " agentes" : ""
              }`,
            };
          case MANAGEMENTS_STATE_REJECTED:
            return {
              title:
                "Gestiones.Lo sentimos, no se ha podido procesar su solicitud",
              description: `Gestiones.Descripción final gestión${
                isAgent ? " agentes" : ""
              }`,
            };
          case MANAGEMENTS_STATE_ERROR:
            return {
              title: "Gestiones.Parece que ha habido un error",
              description:
                "Gestiones.Parece que ha habido un error, prueba de nuevo más tarde",
            };
          default:
            return {
              title: "Gestiones.Estamos procesando su solicitud",
              description: `Gestiones.Descripción final gestión${
                isAgent ? " agentes" : ""
              }`,
              descriptionExtra: "",
            };
        }
      }

      return { title: "", description: "" };
  }
};

const initialManagementRequestInfo: ManagementRequestInfo = {
  refferenceNumber: "-",
  registrarionDate: "-",
  closingDate: "-",
  cupsInfo: {
    cupsCode: "-",
    address: "-",
    environment: "-",
  },
};
const initialRequestStateData: RequestStateDescriptionData = {
  title: "",
  description: "",
  descriptionExtra: "",
};

const initialManagementDetail: ManagementDetail = {
  refferenceNumber: null,
  registrarionDate: "",
  closingDate: null,
  cupsCode: "",
  state: "",
  solutionText: null,
  documents: [],
  incidenceText: "",
  registerType: "",
  address: "",
  environment: "",
};

const initialDocumentData = {
  DocumentoData: "",
  FileName: "",
  IdDocumento: "",
  Nombre: "",
};

const initialManagementCupsInfo: ManagementCupsInfo = {
  cupsCode: "",
  address: "",
  environment: "",
};

const getBase64 = (file: File) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result?.toString());
    reader.onerror = reject;
  });

const incedenceTexts = [
  MANAGEMENTS_INCIDENCE_TEXT_UPDATE_CONTACT,
  MANAGEMENTS_INCIDENCE_TEXT_UPDATE_DELIVERY_DATA,
];

const localStorageHistoricFilters =
  process.env.REACT_APP_LOCALSTORAGE_HISTORIC_FILTERS!;

const manageLocalStorageHistoricFilters = () => {
  const removeLocalstorageHistoricFilters = () =>
    localStorage.removeItem(localStorageHistoricFilters);

  const getLoacalstorageHistoricFilters = () =>
    localStorage.getItem(localStorageHistoricFilters);

  const refreshLocalstorageHistoricFilters = (
    historicFilters: HistoricFilters,
  ) =>
    localStorage.setItem(
      localStorageHistoricFilters,
      JSON.stringify(historicFilters),
    );

  const setLocalstorageHistoricFilters = (historicFilters: HistoricFilters) => {
    localStorage.setItem(
      localStorageHistoricFilters,
      JSON.stringify(historicFilters),
    );
  };

  return {
    removeLocalstorageHistoricFilters,
    getLoacalstorageHistoricFilters,
    refreshLocalstorageHistoricFilters,
    setLocalstorageHistoricFilters,
  };
};

const ibanCodesLengths = [
  { country: "DE", length: 22 }, // Alemania
  { country: "AT", length: 20 }, // Austria
  { country: "BE", length: 16 }, // Bélgica
  { country: "BG", length: 22 }, // Bulgaria
  { country: "CY", length: 28 }, // Chipre
  { country: "HR", length: 21 }, // Croacia
  { country: "DK", length: 18 }, // Dinamarca
  { country: "SK", length: 24 }, // Eslovaquia
  { country: "SI", length: 19 }, // Eslovenia
  { country: "ES", length: 24 }, // España
  { country: "EE", length: 20 }, // Estonia
  { country: "FI", length: 18 }, // Finlandia
  { country: "FR", length: 27 }, // Francia
  { country: "GR", length: 27 }, // Grecia
  { country: "HU", length: 28 }, // Hungría
  { country: "IE", length: 22 }, // Irlanda
  { country: "IS", length: 26 }, // Islandia
  { country: "IT", length: 27 }, // Italia
  { country: "LI", length: 21 }, // Liechtenstein
  { country: "LV", length: 21 }, // Letonia
  { country: "LT", length: 20 }, // Lituania
  { country: "LU", length: 20 }, // Luxemburgo
  { country: "MT", length: 31 }, // Malta
  { country: "MC", length: 27 }, // Mónaco
  { country: "NO", length: 15 }, // Noruega
  { country: "NL", length: 18 }, // Países Bajos
  { country: "PL", length: 28 }, // Polonia
  { country: "PT", length: 25 }, // Portugal
  { country: "GB", length: 22 }, // Reino Unido
  { country: "CZ", length: 24 }, // República Checa
  { country: "RO", length: 24 }, // Rumanía
  { country: "SM", length: 27 }, // San Marino
  { country: "SE", length: 24 }, // Suecia
  { country: "CH", length: 21 }, // Suiza
];

const validateIban = (iban: string): boolean => {
  if (!iban || typeof iban !== "string") return false;
  const ibanClean = iban.replace(/\s+/g, "").toUpperCase();
  if (ibanClean.length < 4) return false;

  // Validar longitud según país
  const country = ibanClean.slice(0, 2);
  const countryData = ibanCodesLengths.find((c) => c.country === country);
  if (!countryData || ibanClean.length !== countryData.length) return false;

  // Reordenar: los 4 primeros al final
  const rearranged = ibanClean.slice(4) + ibanClean.slice(0, 4);

  // Convertir letras a números (A=10, B=11, ..., Z=35)
  let numericIban = "";
  for (let i = 0; i < rearranged.length; i++) {
    const char = rearranged[i];
    if (char >= "A" && char <= "Z") {
      numericIban += (char.charCodeAt(0) - 55).toString();
    } else if (char >= "0" && char <= "9") {
      numericIban += char;
    } else {
      return false;
    }
  }

  // Módulo 97 (usar bloques para evitar overflow)
  let remainder = 0;
  for (let i = 0; i < numericIban.length; i += 9) {
    // Concatenar como string para evitar suma numérica
    const block = String(remainder) + numericIban.slice(i, i + 9);
    remainder = parseInt(block, 10) % 97;
  }
  return remainder === 1;
};

export {
  formatManagements,
  formatManagementsList,
  formatManagementDetail,
  formatOwnerDataCup,
  getManagementCodeTextIcon,
  initialManagementRequestInfo,
  initialManagementDetail,
  initialRequestStateData,
  getManagementStateData,
  getManagementsRequestTypeData,
  getRegisterTypeId,
  getBase64,
  getManagementTitleText,
  initialDocumentData,
  getIncidenceIconTitle,
  incedenceTexts,
  manageLocalStorageHistoricFilters,
  managementCodeText,
  initialManagementCupsInfo,
  getManagementSubtypeTextIcon,
  getIbanStateData,
  validateIban,
};
